import React, { useCallback, useEffect, useMemo } from "react";
import {
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
} from "@mui/material";
import { styled } from "@mui/system";
import Divider from "@mui/material/Divider";
import { useProcessedDataAndCountPreviewLazyQuery } from "graphql/types/graphql";
import { context } from "utils/Provider";
import { useTable } from "react-table";
import { useHeadroomContext } from "../context/HeadRoomProvider";
import { EllipsisWithTooltip } from "./EllipsisWithTooltip";
import { FILTERED_COLUMNS } from "../common/constants";
import { cruxyColors } from "../theme/color-palette";
import colors from "assets/theme-dark/base/colors";
import TruncatedText from "./TruncatedText";

interface ProcessedDataPreviewProps {
  tableWidth?: string;
}

const ProcessedDataPreview: React.FC<ProcessedDataPreviewProps> = ({ tableWidth }) => {
  const { selectedCube } = React.useContext(context);
  const { tableName } = useHeadroomContext();

  const [fetchProcessedDataPreview, { loading, error, data }] = useProcessedDataAndCountPreviewLazyQuery({
    notifyOnNetworkStatusChange: true,
  });

  const loadProcessedDataPreview = useCallback(() => {
    fetchProcessedDataPreview({
      variables: {
        filter: {
          cubeId: selectedCube.cubeId,
          tableName: tableName,
        },
      },
    });
  }, [selectedCube.cubeId, tableName, fetchProcessedDataPreview]);

  useEffect(() => {
    if (!selectedCube.cubeId || !tableName) return;
    loadProcessedDataPreview();
  }, [selectedCube.cubeId, tableName, loadProcessedDataPreview]);

  const tableData = useMemo(() => {
    return data?.processedDataAndCountPreview?.data ?? [];
  }, [data]);

  const columns = useMemo(() => {
    if (!tableData || tableData.length === 0) return [];

    const firstRow = tableData[0];
    return Object.keys(firstRow)
      ?.filter((key) => !FILTERED_COLUMNS.includes(key))
      ?.map((key, index) => {
        const validKey = key === "" || !key ? `Unnamed_Column_${index + 1}` : key;
        return {
          Header: validKey,
          accessor: validKey,
        };
      });
  }, [tableData]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({ columns, data: tableData });

  if (loading) {
    return (
      <StyledCard tableWidth={tableWidth}>
        <Box display="flex" justifyContent="center" alignItems="center" height="80px">
          <CircularProgress sx={{ color: colors.grey[300] }} size={24} />
        </Box>
      </StyledCard>
    );
  }

  if (error) {
    return (
      <StyledCard tableWidth={tableWidth}>
        <Box sx={{ color: cruxyColors.red.main, textAlign: "center", fontSize: "0.85rem", mt: 1 }}>
          Failed to load data: {error.message}
        </Box>
      </StyledCard>
    );
  }

  return (
    <StyledCard tableWidth={tableWidth}>
      <Typography variant="h6" fontWeight="medium" sx={{ color: colors.text.main, fontSize: "1rem", mb: 0.5 }}>
        Processed Data Preview
      </Typography>
      <Divider sx={{ my: 0.5, borderColor: colors.grey[300] }} />
      <Typography variant="body2" sx={{ color: colors.text.main, fontSize: "0.85rem", mb: 0.5 }}>
        Name: {tableName}
      </Typography>
      <Typography variant="body2" sx={{ color: colors.text.main, fontSize: "0.85rem", mb: 1 }}>
        Total count: {data?.processedDataAndCountPreview?.totalCount ?? "N/A"}
      </Typography>
      {tableData?.length > 0 ? (
        <Box sx={{ maxHeight: "250px", overflowY: "auto" }}>
          <StyledTableContainer>
            <Table {...getTableProps()} size="small" aria-label="dataset preview table">
              <TableHead>
                {headerGroups?.map((headerGroup, groupIndex) => (
                  <TableRow {...headerGroup.getHeaderGroupProps()} key={`header-group-${groupIndex}`}>
                    {headerGroup.headers?.map((column, colIndex) => (
                      <TableCell
                        {...column.getHeaderProps()}
                        key={`header-${groupIndex}-${colIndex}`}
                        align="left"
                        sx={{
                          backgroundColor: cruxyColors.blue.main,
                          color: "#fff",
                          fontWeight: 500,
                          fontSize: "0.85rem",
                          padding: "6px 10px",
                        }}
                      >
                        <EllipsisWithTooltip placement="top">{column.render("Header")}</EllipsisWithTooltip>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableHead>
              <TableBody {...getTableBodyProps()}>
                {rows?.map((row, rowIndex) => {
                  prepareRow(row);
                  return (
                    <TableRow {...row.getRowProps()} key={`row-${rowIndex}`}>
                      {row.cells?.map((cell, cellIndex) => (
                        <TableCell
                          {...cell.getCellProps()}
                          key={`cell-${rowIndex}-${cellIndex}`}
                          align="left"
                          sx={{
                            padding: "6px 10px",
                            fontSize: "0.85rem",
                            color: colors.text.main,
                            borderColor: colors.grey[200],
                          }}
                        >
                          <TruncatedText text={cell.value == null ? "" : String(cell.value)} maxLength={15} />
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </StyledTableContainer>
        </Box>
      ) : (
        <Box sx={{ color: "#6b7280", textAlign: "center", fontSize: "0.85rem", mt: 1 }}>No data available</Box>
      )}
    </StyledCard>
  );
};

const StyledCard = styled(Box)<{ tableWidth?: string }>(({ tableWidth }) => ({
  width: tableWidth || "100%",
  background: "#fff",
  borderRadius: "6px",
  padding: "12px",
  border: "1px solid #d1d5db",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  backgroundColor: "#fff",
  borderRadius: "6px",
  border: "1px solid #d1d5db",
  overflowX: "auto",
}));

export default ProcessedDataPreview;
